/**
 * Unified Contact Manager → wholesale account pipeline (London Smoke & Cure):
 * account creation (Auth + users doc) + optional welcome email + list chips / lead stage.
 *
 * Welcome send: `sendAccountWelcomePromoteEmail` in emailtransporter (template key `account_welcome_promote`).
 */
import { sendAccountWelcomePromoteEmail } from '../pages/ScaleComponents/emailtransporter';
import { resolveUserPhone } from '../utils/userPhoneFields';

export const WHOLESALE_PUBLIC_BASE = 'https://wholesale.londonsmokeandcure.co.uk';

/** Stored on list recipients as chips after a successful promote step */
export const PROMOTE_PIPELINE_CHIPS = {
  WELCOME_SENT: 'Welcome email sent',
  PROFILE_DETAILS_REQUESTED: 'Profile: details requested',
};

/**
 * Fields we encourage the new customer to complete if missing on the contact row.
 * @param {object} rec - list recipient / merged row
 * @returns {string[]} human-readable gap labels
 */
export function assessProfileGaps(rec) {
  if (!rec || typeof rec !== 'object') return [];
  const gaps = [];
  if (!(rec.company || rec['Company Name'] || '').toString().trim()) {
    gaps.push('company name');
  }
  if (!(rec.phone || rec.Phone || '').toString().trim()) {
    gaps.push('phone');
  }
  if (!(rec.city || rec.City || '').toString().trim()) {
    gaps.push('city / delivery area');
  }
  return gaps;
}

function formatAddressForMerge(addr) {
  if (!addr || typeof addr !== 'object') return '';
  const parts = [addr.line1, addr.line2, addr.line3, addr.city, addr.postCode].filter(
    (p) => p != null && String(p).trim() !== '',
  );
  return parts.join(', ');
}

/**
 * Merge payload for welcome email placeholders (combined with list row by sendMailMergeEmail).
 * Pass `profile` (Firestore-shaped user fields from `buildUserProfileFromContact`) and `contact` (list row)
 * so templates always receive names and account details even if the list row lookup lags.
 */
export function buildPromoteWelcomeMergeInfo({
  username,
  tempPassword,
  profileGaps = [],
  profile = {},
  contact = {},
}) {
  const loginUrl = `${WHOLESALE_PUBLIC_BASE}/login`;
  const accountUrl = `${WHOLESALE_PUBLIC_BASE}/account`;
  const productsUrl = `${WHOLESALE_PUBLIC_BASE}/products`;

  const firstFromContact = String(contact.firstName || contact['First Name'] || '').trim();
  const lastFromContact = String(contact.lastName || contact['Last Name'] || '').trim();
  const fullFromProfile = String(profile.fullName || '').trim();
  const nameParts = fullFromProfile.split(/\s+/).filter(Boolean);
  const firstName =
    firstFromContact ||
    (nameParts.length ? nameParts[0] : '') ||
    String(contact.company || contact['Company Name'] || profile.companyName || '').trim() ||
    '';
  const lastName =
    lastFromContact || (nameParts.length > 1 ? nameParts.slice(1).join(' ') : '');

  const companyName = String(
    profile.companyName || contact.company || contact['Company Name'] || '',
  ).trim();

  const phoneRaw = resolveUserPhone(profile);
  const phoneLineHtml = phoneRaw
    ? `<p style="margin:0 0 16px 0;font-size:14px;color:#333;"><strong>Phone:</strong> ${phoneRaw.replace(/</g, '&lt;')}</p>`
    : '';
  const phoneLinePlain = phoneRaw ? `Phone: ${phoneRaw}` : '';
  /** Invisible placeholder when empty so --phone line-- / --phone text-- merge without leaving raw tokens */
  const ZWSP = '\u200B';

  let profileExtraNote =
    '<p style="color:#4a4a4a;font-size:14px;line-height:1.6;margin:24px 0 0 0;">You can add or update delivery and billing details any time under <a href="' +
    accountUrl +
    '" style="color:#B99040;font-weight:500;">My account</a>.</p>';

  if (profileGaps.length) {
    profileExtraNote =
      '<div style="border-left:4px solid #B99040;padding:16px 18px;margin:24px 0 0 0;background:rgba(185,144,64,0.08);border-radius:0 8px 8px 0;">' +
      '<p style="margin:0 0 8px 0;font-weight:600;color:#1a1a1a;font-size:15px;">Please complete a few details</p>' +
      '<p style="margin:0;color:#4a4a4a;font-size:14px;line-height:1.6;">After you sign in, update your account or reply to this email with: <strong>' +
      profileGaps.join(', ') +
      '</strong>.</p></div>';
  }

  const winS = String(profile.windowStart || '').trim();
  const winE = String(profile.windowEnd || '').trim();
  const windowRange = [winS, winE].filter(Boolean).join(' – ');

  return {
    username: username || '',
    'temp password': tempPassword || '',
    'login url': loginUrl,
    'account url': accountUrl,
    'products url': productsUrl,
    'profile extra note': profileExtraNote,
    'First Name': firstName,
    'Last Name': lastName,
    'Company Name': companyName,
    companyName,
    fullName: fullFromProfile || [firstName, lastName].filter(Boolean).join(' ') || companyName,
    email: String(profile.email || contact.email || contact.Email || '').trim(),
    contactInfo: phoneRaw,
    'phone line': phoneLineHtml || ZWSP,
    'phone text': phoneLinePlain || ZWSP,
    Pricing: profile.Pricing != null ? String(profile.Pricing) : '',
    role: String(profile.role || '').trim(),
    packingSlipNote: String(profile.packingSlipNote || '').trim(),
    poSystem: String(profile.poSystem || '').trim(),
    'billing address': formatAddressForMerge(profile.billingAddress),
    'shipping address': formatAddressForMerge(profile.shippingAddress),
    'window range': windowRange,
  };
}

/**
 * Sends the promote welcome email (single recipient). Non-fatal errors should be caught by caller.
 */
export async function sendContactPromoteWelcomeEmail({
  email,
  username,
  tempPassword,
  profileGaps,
  profile,
  contact,
}) {
  const info = buildPromoteWelcomeMergeInfo({
    username,
    tempPassword,
    profileGaps,
    profile,
    contact,
  });
  return sendAccountWelcomePromoteEmail({ to: email, info });
}
